import { parseCalendarDate } from '@/shared/lib/zonedTime';

import { hasPeriod, parsePeriod, type SearchPeriod } from './period';

/** The filters of the results page as the URL keeps them: `author=<user id>`, `period=day|week|month|year|custom`, `from`/`to`. */
export interface UrlFilters extends SearchPeriod {
  author: string | null;
}

export const NO_URL_FILTERS: UrlFilters = { author: null, period: null, from: null, to: null };

const FILTER_KEYS = ['author', 'period', 'from', 'to'] as const;

const dateOrNull = (value: string | null): string | null => (parseCalendarDate(value) ? value : null);

/** Reads the filters from the URL (anything malformed counts as not set). */
export function readFilters(params: URLSearchParams): UrlFilters {
  const period = parsePeriod(params.get('period'));
  const author = params.get('author')?.trim() ?? '';
  return {
    author: author.length > 0 ? author : null,
    period,
    from: period === 'custom' ? dateOrNull(params.get('from')) : null,
    to: period === 'custom' ? dateOrNull(params.get('to')) : null,
  };
}

/** A copy of `params` with the changed filters written (null removes one) and the page reset. */
export function writeFilters(params: URLSearchParams, changes: Partial<UrlFilters>): URLSearchParams {
  const next = new URLSearchParams(params);
  const merged: UrlFilters = { ...readFilters(params), ...changes };
  const values: Record<(typeof FILTER_KEYS)[number], string | null> = {
    author: merged.author,
    period: merged.period,
    from: merged.period === 'custom' ? merged.from : null,
    to: merged.period === 'custom' ? merged.to : null,
  };
  for (const key of FILTER_KEYS) {
    const value = values[key];
    if (value) next.set(key, value);
    else next.delete(key);
  }
  next.delete('page');
  return next;
}

/** How many filters narrow the search (the author, the period). */
export const countFilters = (filters: UrlFilters): number =>
  (filters.author ? 1 : 0) + (hasPeriod(filters) ? 1 : 0);

/** The `/search` URL for a query with filters (the palette's "search everywhere"). */
export function searchUrl(
  q: string,
  options: { module?: string | null; author?: string | null; period?: SearchPeriod['period'] } = {},
): string {
  const parts = [`q=${encodeURIComponent(q)}`];
  if (options.module) parts.push(`module=${encodeURIComponent(options.module)}`);
  if (options.author) parts.push(`author=${encodeURIComponent(options.author)}`);
  if (options.period && options.period !== 'custom') parts.push(`period=${options.period}`);
  return `/search?${parts.join('&')}`;
}
